/**
 * The week strip: one ring per day for the last few days, today last.
 *
 * Reads the completions-per-day series /api/user-analytics/daily-completions
 * already returns (habits from the execution plane + finished tasks and
 * steps, bucketed by the person's local day). A ring's fill is that day's
 * count against the best day in the window — the same "rhythm" the old bars
 * drew, in the shape the 2026-09-12 mockup asked for. It is a rhythm, not a
 * score: a quiet week reads as a row of light rings, never as a percentage.
 *
 * Pure and import-free so the node probe can pin it.
 */

export interface DaySeries {
  /** YYYY-MM-DD, the person's local day. */
  date: string
  habits: number
  items: number
}

export interface WeekRing {
  date: string
  /** One letter for the weekday, in the viewer's locale. */
  label: string
  total: number
  /** 0..1 — this day against the window's best day. */
  fill: number
  isToday: boolean
}

/**
 * Empty when the whole window is zero: no decorative rings, ever. `today`
 * names today's ring by date; when no entry matches (a clock skew across
 * midnight), the last entry is today, as the server orders it.
 */
export function weekRings(series: DaySeries[], today: string): WeekRing[] {
  if (!Array.isArray(series) || series.length === 0) return []
  const totals = series.map(d => Math.max(0, (Number(d.habits) || 0) + (Number(d.items) || 0)))
  const max = Math.max(...totals)
  if (max <= 0) return []
  const matched = series.some(d => d.date === today)
  return series.map((d, i) => ({
    date: d.date,
    label: weekdayInitial(d.date),
    total: totals[i],
    fill: totals[i] / max,
    isToday: matched ? d.date === today : i === series.length - 1,
  }))
}

/** "T" for Tuesday in English; whatever the locale's one-letter form is. */
export function weekdayInitial(dateISO: string): string {
  const [y, m, d] = dateISO.split('-').map(Number)
  if (!y || !m || !d) return ''
  try {
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'narrow' })
  } catch {
    return ''
  }
}
